import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/styles/select-controls.css";
import "@/pages/toto-home.css";
import "@/pages/toto-unified-surface.css";
import App from "@/App";
import "@/styles/toto-ivory-material.css";
import "@/styles/vessel-primary-navigation.css";
import "@/styles/vessel-frame.css";
import "@/styles/journey-editor-layout.css";
import "@/styles/floor-plan-compact.css";
import { applyStoredReadingMode } from "@/components/ReadingModeToggle";
import CodespaceNavigationTrace from "@/components/CodespaceNavigationTrace";

// Apply the persisted reading mode before React mounts, so there's no
// flash of the wrong mode on first paint.
applyStoredReadingMode();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

class PreviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the browser console useful for Playwright / developer inspection.
    console.error("SONMI render failure", error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 32,
          background: "#fafaf7",
          color: "#292524",
          fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <section style={{ maxWidth: 680 }}>
          <div style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "#78716c" }}>
            SONMI · Preview diagnostic
          </div>
          <h1 style={{ margin: "14px 0 10px", fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400, fontSize: 34 }}>
            The preview hit a rendering error.
          </h1>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: "#57534e" }}>
            The project is still intact. This diagnostic replaces the blank screen so the failing component can be repaired directly.
          </p>
          <pre
            style={{
              marginTop: 22,
              padding: 18,
              border: "1px solid #e7e5e4",
              borderRadius: 16,
              background: "rgba(255,255,255,.75)",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              fontSize: 12,
              lineHeight: 1.55,
              color: "#44403c",
            }}
          >
            {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
          </pre>
        </section>
      </main>
    );
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <PreviewErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
        <CodespaceNavigationTrace />
      </QueryClientProvider>
    </PreviewErrorBoundary>
  </React.StrictMode>,
);
